import { useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Render } from "@puckeditor/core";
import {
  puckConfig,
  Navigation,
  Footer,
  MobileStickyCall,
  ClaimBusinessBanner,
  installInteractionTracking,
  isPreviewMode,
  resolveNavigationStyleForPage,
  visibleSections,
  utmFromUrl,
  getSessionId,
  useJobsiteWebMcp,
} from "@servicepro/jobsite-components";
import { PagesProvider } from "./contexts/pages-context";
import { SEOHead } from "./components/seo-head";
import { NotFound } from "./pages/not-found";
import { mergeMetadata } from "./types/seo";
import siteData from "virtual:site-data";

// Global config for tracking and forms (client-side only)
if (typeof window !== "undefined") {
  (window as any).__SERVICEPRO_CONFIG__ = {
    apiUrl: siteData.apiUrl,
    // SP-400; undefined on an older build, which disables the fallback rather than breaking.
    backupApiUrl: siteData.backupApiUrl,
    siteId: siteData.siteId,
    slug: siteData.slug,
    variant: siteData.variant,
    businessId: siteData.businessId,
    business: siteData.business,
    turnstileSiteKey: siteData.turnstileSiteKey,
    googleMapsApiKey: siteData.googleMapsApiKey,
    // A transparent header is fixed with no spacer, so the first in-flow
    // section has to know to clear it or it renders underneath.
    navigationStyle: siteData.navigationConfig?.style,
  };
}

/**
 * Route tracker for analytics
 */
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    // Skip analytics tracking in preview mode (e.g. owner viewing their own site)
    if (isPreviewMode()) return;

    const config = (window as any).__SERVICEPRO_CONFIG__;
    const trackUrl = config?.apiUrl && `${config.apiUrl}/api/analytics/${config.siteId}/pageview`;

    if (!trackUrl) return;

    const sessionId = getSessionId("sp_session_id");

    fetch(trackUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        path: location.pathname,
        sessionId,
        variant: config?.variant,
        // Source attribution: the external referrer + any UTM, used server-side
        // only when the session is first created (organic search has no UTM, so
        // the referrer is what distinguishes it from direct).
        referrer: document.referrer || undefined,
        utmParams: utmFromUrl(),
      }),
    }).catch(() => {
      console.error("service_pro_error_code: 1991");
    });
  }, [location.pathname]);

  return null;
}

/** Components (Hero, EmergencyBand) read navigationStyle from the window
 *  config to pad for a fixed transparent nav; it has to reflect the CURRENT
 *  page's effective style. Mutating during render is deliberate: the readers
 *  render after this node in the same pass, and effects would be too late. */
function ApplyPageNavStyle({ style }: { style: string | undefined }) {
  if (typeof window !== "undefined") {
    const config = (window as { __SERVICEPRO_CONFIG__?: Record<string, unknown> })
      .__SERVICEPRO_CONFIG__;
    if (config) {
      config.navigationStyle = style;
    }
  }
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

function AppRouter() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target?.closest("a");

      if (!link) return;

      const href = link.getAttribute("href");
      if (!href) return;

      const isInternal = href.startsWith("/") && !href.startsWith("//");
      const isAnchor = href.startsWith("#");
      const isExternal = /^(https?:|mailto:|tel:)/.test(href);

      if (!isInternal || isExternal || isAnchor) return;

      // Allow opening in new tab with cmd/ctrl click
      if ((e as any).ctrlKey || (e as any).metaKey || (e as any).shiftKey) return;

      e.preventDefault();
      navigate(href);
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [navigate]);

  // Install on-site interaction tracking (CTA taps: call/text/directions/email).
  // One delegated listener; tel:/mailto:/maps links are caught by inference, so
  // no per-CTA wiring is needed. Shares the pageview session id.
  useEffect(() => {
    const config = (window as any).__SERVICEPRO_CONFIG__;
    if (!config?.apiUrl || !config?.siteId) return;
    const sessionId = getSessionId("sp_session_id");
    return installInteractionTracking({
      endpoint: `${config.apiUrl}/api/analytics/${config.siteId}/event`,
      sessionId,
    });
  }, []);

  // Offer the site's own flows — services, availability, quote requests,
  // booking — to a browsing agent as WebMCP tools, so it doesn't have to drive
  // the page to get a customer booked. No-op unless the browser supports it.
  useJobsiteWebMcp();

  const { pages, seoMetadata, navigationConfig, footerConfig, mobileStickyCallConfig } = siteData;

  if (!pages || pages.length === 0) {
    return <div style={{ padding: "20px" }}>No pages found in site data</div>;
  }

  return (
    <PagesProvider pages={pages.map(({ id, path, title }) => ({ id, path, title }))}>
      <ScrollToTop />

      <Routes>
        {pages.map((page) => {

          const pageMetadata = mergeMetadata(seoMetadata, {
            ...(page.seo || {}),
            canonical: page.seo?.canonical || `${seoMetadata.domain}${page.path}`,
          });

          const builderData = page.builderData ? visibleSections(page.builderData) : null;

          // A transparent nav only holds on pages that open with a Hero; any
          // other first section (old Breadcrumb-first layouts, a hidden Hero)
          // demotes it to solid so the header stays readable.
          const navStyle = resolveNavigationStyleForPage(
            navigationConfig?.style as string | undefined,
            (builderData as { content?: Array<{ type?: string }> } | null)?.content,
          );
          const pageNavigationConfig = navigationConfig
            ? { ...navigationConfig, style: navStyle }
            : navigationConfig;

          return (
            <Route
              key={page.path}
              path={page.path}
              element={
                <>
                  <SEOHead metadata={pageMetadata} faviconUrl={seoMetadata?.faviconUrl} />
                  <div className="jobsite-container jobsite:min-h-screen">
                    {/* Claim banner — only renders when ?claim= token is in URL */}
                    <ClaimBusinessBanner />

                    {/* Render Navigation if config exists */}
                    <ApplyPageNavStyle style={navStyle} />
                    {pageNavigationConfig && <Navigation {...pageNavigationConfig} />}

                    {/* Render page content */}
                    {builderData ? (
                      <Render
                        config={puckConfig as any}
                        data={{
                          ...builderData,
                          root: {
                            ...builderData.root,
                            props: {
                              ...builderData.root?.props,
                              theme: siteData.puckTheme,
                            },
                          },
                        }}
                        metadata={{
                          business: siteData.business,
                          site: {
                            id: siteData.siteId,
                            title: siteData.seoMetadata?.defaultTitle,
                            slug: siteData.slug,
                            url: seoMetadata.domain,
                            googleMapsApiKey: (siteData as any).googleMapsApiKey,
                          },
                          pagePaths: pages.map((p) => p.path),
                        }}
                      />
                    ) : (
                      <div style={{ padding: "20px" }}>No content for page: {page.title}</div>
                    )}

                    {/* Render Footer if config exists */}
                    {footerConfig && <Footer {...footerConfig} />}

                    {/* Render Mobile Sticky Call if config exists. The nav's
                        "Show Text Us" setting is the single source of truth, so
                        the sticky bar follows it (falling back to its own). */}
                    {mobileStickyCallConfig && (
                      <MobileStickyCall
                        {...mobileStickyCallConfig}
                        showTextUs={
                          navigationConfig?.showTextUs ??
                          mobileStickyCallConfig.showTextUs
                        }
                      />
                    )}
                  </div>
                </>
              }
            />
          );
        })}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </PagesProvider>
  );
}

export function App() {
  return (
    <>
      <RouteTracker />
      <AppRouter />
    </>
  );
}
